
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AddressFields } from "@/components/profile/AddressFields";
import { TagSelector, SELECT_ALL_VALUE, NONE_VALUE } from "@/components/seller/meal-form/TagSelector";
const FirstTimeOnboarding = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    foodPreferences: [] as string[],
    allergyConsiderations: [] as string[],
    dietaryPreferences: [] as string[],
    referralSource: "",
    chefPreferences: [] as string[]
  });

  // Available options arrays
  const foodPreferences = ["Air-fried", "Baked Goods", "Oven-baked", "Roasted", "Slow-cooked", "Smoked", "Wood-fired"];
  const allergens = ["Milk", "Eggs", "Fish", "Shellfish", "Tree nuts", "Peanuts", "Wheat", "Soy", "Sesame"];
  const dietaryRestrictions = ["Vegetarian", "Vegan", "Gluten-free", "Dairy-free", "Nut-free", "Halal", "Kosher", "Keto", "Paleo", "Low-carb", "Sugar-free"];
  const chefTypes = ["Home Cook", "Professional Chef", "Pastry Chef", "Caterer", "Health-focused Chef", "Food Truck Owner", "Restaurant Owner"];
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }
        const {
          data: profile,
          error: profileError
        } = await supabase.from('profiles').select('onboarding_completed').eq('id', user.id).single();
        if (profileError) {
          console.error("Error checking onboarding status:", profileError);
          return;
        }
        if (profile?.onboarding_completed) {
          navigate('/');
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      }
    };
    checkOnboardingStatus();
  }, [navigate]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "User not found. Please log in again.",
          variant: "destructive"
        });
        return;
      }
      const {
        error: updateError
      } = await supabase.from('profiles').update({
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        food_preferences: formData.foodPreferences,
        allergy_considerations: formData.allergyConsiderations,
        dietary_preferences: formData.dietaryPreferences,
        chef_preferences: formData.chefPreferences,
        referral_source: formData.referralSource,
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString()
      }).eq('id', user.id);
      if (updateError) {
        console.error("Error updating profile:", updateError);
        toast({
          title: "Error",
          description: "Failed to update profile. Please try again.",
          variant: "destructive"
        });
        return;
      }
      toast({
        title: "Success",
        description: "Your profile has been set up!"
      });
      navigate('/');
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen bg-background">
      <div className="relative h-64">
        <img src="/lovable-uploads/01b2f24f-c958-4392-91ad-c4d34ef0318a.png" alt="Kitchen utensils and cookware" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <h1 className="text-4xl font-bold text-white text-center">
            Welcome to HomePlate
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 -mt-8">
        <div className="bg-card rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Tell us about yourself</h2>
              <p className="text-muted-foreground">We're a community of home chefs and food lovers. Let's get to know you better. </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" type="text" required className="bg-[#EDEDED] text-black" value={formData.firstName} onChange={e => setFormData(prev => ({
                ...prev,
                firstName: e.target.value
              }))} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input id="lastName" type="text" required className="bg-[#EDEDED] text-black" value={formData.lastName} onChange={e => setFormData(prev => ({
                ...prev,
                lastName: e.target.value
              }))} />
              </div>
            </div>

            <AddressFields address={formData.address} city={formData.city} state={formData.state} postalCode={formData.postalCode} onAddressChange={e => setFormData(prev => ({
            ...prev,
            address: e.target.value
          }))} onCityChange={e => setFormData(prev => ({
            ...prev,
            city: e.target.value
          }))} onStateChange={e => setFormData(prev => ({
            ...prev,
            state: e.target.value
          }))} onPostalCodeChange={e => setFormData(prev => ({
            ...prev,
            postalCode: e.target.value
          }))} />

            <div className="space-y-4">
              <Label>What food or meal types are you interested in?</Label>
              <TagSelector
                label="food types"
                selectedTags={formData.foodPreferences}
                availableTags={foodPreferences}
                onTagsChange={tags => setFormData(prev => ({
                  ...prev,
                  foodPreferences: tags
                }))}
                showSelectAll={true}
              />
            </div>
            
            <div className="space-y-4">
              <Label>What types of chefs do you prefer to order from?</Label>
              <TagSelector
                label="chef types"
                selectedTags={formData.chefPreferences}
                availableTags={chefTypes}
                onTagsChange={tags => setFormData(prev => ({
                  ...prev,
                  chefPreferences: tags
                }))}
                showSelectAll={true}
              />
            </div>

            <div className="space-y-4">
              <Label>Do you have any allergy considerations?</Label>
              <TagSelector
                label="allergens"
                selectedTags={formData.allergyConsiderations}
                availableTags={allergens}
                onTagsChange={tags => setFormData(prev => ({
                  ...prev,
                  allergyConsiderations: tags
                }))}
                showNone={true}
              />
            </div>

            <div className="space-y-4">
              <Label>Do you have any dietary restrictions?</Label>
              <TagSelector
                label="dietary restrictions"
                selectedTags={formData.dietaryPreferences}
                availableTags={dietaryRestrictions}
                onTagsChange={tags => setFormData(prev => ({
                  ...prev,
                  dietaryPreferences: tags
                }))}
                showNone={true}
              />
            </div>

            <div className="space-y-4">
              <Label>How did you hear about us?</Label>
              <RadioGroup value={formData.referralSource} onValueChange={value => setFormData(prev => ({
                ...prev,
                referralSource: value
              }))}>
                {["Family or friend", "Facebook", "Google", "Other"].map(source => <div className="flex items-center space-x-2" key={source}>
                    <RadioGroupItem value={source} id={source} />
                    <Label htmlFor={source}>{source}</Label>
                  </div>)}
              </RadioGroup>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50">
              {loading ? "Setting up your profile..." : "Complete Setup"}
            </button>
          </form>
        </div>
      </div>
    </div>;
};
export default FirstTimeOnboarding;
